import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, CheckCircle2 } from 'lucide-react';
import { useLanguage } from './LanguageContext';
type Service = {
  titleKey: string;
  thumbnailUrl: string;
  durationKey: string;
};
type Booking = {
  id: number;
  name: string;
  city: string;
  service: Service;
  minutesAgo: number;
};
const SERVICES_CONFIG: Service[] = [{
  titleKey: 'layer',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/8XEtKzeRXwgahSYKUvB6yk/s1.png',
  durationKey: 'm90'
}, {
  titleKey: 'balayage',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/dQUzXXEXUKs2oUQsy6Q6sV/s3.png',
  durationKey: 'h2m30'
}, {
  titleKey: 'movement',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/cgh4ex9HW3tSNBU3xbyeqG/s4.png',
  durationKey: 'm90'
}, {
  titleKey: 'pixie',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/cVvAwQHY7zPZZLQZ93X9pg/s2.png',
  durationKey: 'm60'
}, {
  titleKey: 'mens',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/9GoWNjcZqepz7oXQkXuXUB/s5.png',
  durationKey: 'm45'
}, {
  titleKey: 'special',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/8XEtKzeRXwgahSYKUvB6yk/s1.png',
  durationKey: 'm90'
}, {
  titleKey: 'facial',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/bDtmL188rUszXump9CTVi2/219_pictures_conexion_007.jpg',
  durationKey: 'm60'
}, {
  titleKey: 'massage',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/5rEbyLiQ77rMNfJwjHZsXf/315_Screen_Shot_2017-05-31_at_8.50.29_PM.png',
  durationKey: 'm60'
}];
const NAMES = ['Maria G.', 'Sofia L.', 'Carla M.', 'Diana P.', 'Jessica R.', 'Isabella T.', 'Camila V.', 'Valentina A.', 'Lucia H.', 'Daniela S.', 'Andrea C.', 'Gabriela N.', 'Ana F.', 'Patricia O.', 'Rosa E.', 'Michelle K.', 'Vanessa B.', 'Alex T.', 'Sam D.', 'Jordan W.', 'Natalia R.', 'Emily H.', 'Sarah J.', 'Laura M.', 'Karina V.', 'Adriana L.', 'Monica P.', 'Yesenia G.', 'Brenda C.', 'Stephanie A.'];
const CITIES = ['Roscoe Village', 'Lakeview', 'Lincoln Park', 'Wicker Park', 'Logan Square', 'Pilsen', 'Humboldt Park', 'Avondale', 'Bucktown', 'Albany Park', 'Chicago, IL', 'Naperville, IL', 'Evanston, IL', 'Oak Park, IL', 'Cicero, IL', 'Skokie, IL', 'Berwyn, IL', 'Aurora, IL', 'Schaumburg, IL', 'Elgin, IL'];
const pickRandom = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
export function BookingToast() {
  const {
    t
  } = useLanguage();
  const [current, setCurrent] = useState<Booking | null>(null);
  const recentNames = useRef<string[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idCounter = useRef(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);
  const pickName = (): string => {
    const recent = recentNames.current;
    const available = NAMES.filter(n => !recent.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const choice = pickRandom(pool);
    recentNames.current = [...recent, choice].slice(-10);
    return choice;
  };
  const showNext = () => {
    const booking: Booking = {
      id: ++idCounter.current,
      name: pickName(),
      city: pickRandom(CITIES),
      service: pickRandom(SERVICES_CONFIG),
      minutesAgo: Math.floor(Math.random() * 12) + 1
    };
    setCurrent(booking);
    timeoutRef.current = setTimeout(() => {
      setCurrent(null);
      const nextDelay = 8000 + Math.random() * 7000;
      timeoutRef.current = setTimeout(showNext, nextDelay);
    }, 4000);
  };
  useEffect(() => {
    timeoutRef.current = setTimeout(showNext, 3000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
  const motionProps = reducedMotion ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    },
    transition: {
      duration: 0.2
    }
  } : {
    initial: {
      opacity: 0,
      y: 40,
      scale: 0.96
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    exit: {
      opacity: 0,
      y: 40,
      scale: 0.96
    },
    transition: {
      duration: 0.45,
      ease: [0.32, 0.72, 0, 1] as const
    }
  };
  return <div aria-live="polite" aria-atomic="true" className="fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-2rem)] max-w-sm pointer-events-none" data-id="element-383">
      <AnimatePresence mode="wait" data-id="element-384">
        {current && <motion.div key={current.id} {...motionProps} className="pointer-events-auto bg-white rounded-xl shadow-2xl border border-brand-brown/5 overflow-hidden" data-id="element-385">
            <div className="flex items-stretch gap-3 p-3" data-id="element-386">
              <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden flex-shrink-0" data-id="element-387">
                <img src={current.service.thumbnailUrl} alt={t(`toast.services.${current.service.titleKey}`)} className="w-full h-full object-cover object-center" data-id="element-388" />
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-white" data-id="element-389">
                  <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={3} data-id="element-390" />
                </div>
              </div>

              <div className="flex-1 min-w-0 flex flex-col justify-center" data-id="element-391">
                <p className="text-[13px] sm:text-sm text-brand-brown leading-snug" data-id="element-392">
                  <span className="font-semibold" data-id="element-393">{current.name}</span>{' '}
                  <span className="text-brand-brown/60" data-id="element-394">{t('toast.in')}</span>{' '}
                  <span className="font-medium" data-id="element-395">{current.city}</span>
                </p>
                <p className="text-[13px] sm:text-sm text-brand-brown/80 leading-snug truncate" data-id="element-396">
                  {t('toast.justBooked')}{' '}
                  <span className="text-brand-terracotta font-medium" data-id="element-397">
                    {t(`toast.services.${current.service.titleKey}`)}
                  </span>
                </p>
                <div className="flex items-center gap-1.5 mt-1 text-[11px] text-brand-brown/50" data-id="element-398">
                  <Calendar className="w-3 h-3" data-id="element-399" />
                  <span data-id="element-400">
                    {current.minutesAgo} {t('toast.minAgo')}
                  </span>
                  <span className="mx-1" data-id="element-401">·</span>
                  <span data-id="element-402">
                    {t(`toast.duration.${current.service.durationKey}`)}
                  </span>
                </div>
              </div>
            </div>

            {!reducedMotion && <motion.div initial={{
          scaleX: 1
        }} animate={{
          scaleX: 0
        }} transition={{
          duration: 4,
          ease: 'linear'
        }} style={{
          transformOrigin: 'left'
        }} className="h-0.5 bg-brand-terracotta/70" data-id="element-403" />}
          </motion.div>}
      </AnimatePresence>
    </div>;
}